import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { AvailabilityCalendar } from './components/AvailabilityCalendar';
import { HotelOverview } from './components/HotelOverview';
import { fetchAvailability, fetchHotelOverview } from './utils/apiClient';
import { trackWidgetLoad, trackWidgetError, trackViewHotel, trackSelectRoom } from './utils/tracking';
import type { MomentjeWidgetConfig, MomentjeHotelOverviewConfig, StockItem, HotelOverviewData } from './types';
import './styles/calendar.css';

interface WidgetWrapperProps extends MomentjeWidgetConfig {}

/**
 * Internal wrapper component that handles data fetching and state
 */
function WidgetWrapper(props: WidgetWrapperProps) {
  const {
    apiUrl,
    hotelSlug,
    roomId,
    locale = 'en',
    bookingUrl,
    onDateSelect,
    onError,
    onLoadingChange,
    initialArrival,
    initialDeparture,
  } = props;

  const [availabilityData, setAvailabilityData] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [roomInfo, setRoomInfo] = useState<{ name: string; description: string } | null>(null);
  const [hotelInfo, setHotelInfo] = useState<{ name: string } | null>(null);
  const [apiBookingUrl, setApiBookingUrl] = useState<string | null>(null);
  const loadStartTime = useRef<number>(Date.now());

  useEffect(() => {
    async function loadAvailability() {
      setLoading(true);
      onLoadingChange?.(true);

      try {
        // Fetch data for 1.5 years from today
        const today = new Date();
        const oneAndHalfYearsFromToday = new Date(
          today.getFullYear() + 1,
          today.getMonth() + 6,
          today.getDate()
        );

        const startDate = today.toISOString().split('T')[0];
        const endDate = oneAndHalfYearsFromToday.toISOString().split('T')[0];

        const response = await fetchAvailability({
          apiUrl,
          hotelSlug,
          roomId,
          startDate,
          endDate,
          locale,
        });

        setAvailabilityData(response.data);
        setRoomInfo({ name: response.room.name, description: response.room.description });
        setHotelInfo({ name: response.hotel.name });
        setApiBookingUrl(response.bookingUrl || null);
        setError(null);

        // Track successful load
        const loadTimeMs = Date.now() - loadStartTime.current;
        trackWidgetLoad(hotelSlug, roomId, loadTimeMs);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load availability');
        setError(error);
        onError?.(error);

        // Track error
        trackWidgetError(error.message, hotelSlug, roomId);
      } finally {
        setLoading(false);
        onLoadingChange?.(false);
      }
    }

    loadAvailability();
  }, [apiUrl, hotelSlug, roomId, locale, onError, onLoadingChange]);

  if (loading) {
    return (
      <div className="momentje-widget">
        <div className="w-full max-w-6xl mx-auto border rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="text-gray-500 mb-2">Loading availability...</div>
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="momentje-widget">
        <div className="w-full max-w-6xl mx-auto border rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-900">Error</h3>
          </div>
          <div className="flex items-center justify-center p-8 text-center">
            <div className="max-w-md">
              <div className="text-red-600 mb-4">
                <svg
                  className="w-12 h-12 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Calendar</h3>
              <p className="text-gray-600 text-sm mb-6">{error.message}</p>
              {bookingUrl && (
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  View Booking Options
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!roomInfo || !hotelInfo || availabilityData.length === 0) {
    return (
      <div className="momentje-widget">
        <div className="w-full max-w-6xl mx-auto border rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-900">No Availability</h3>
          </div>
          <div className="flex items-center justify-center p-8 text-center">
            <div className="max-w-md">
              <p className="text-gray-600 text-sm mb-6">
                No availability data found for this room. Please try again later.
              </p>
              {bookingUrl && (
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  View Booking Options
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use API-provided bookingUrl if no prop was passed
  const effectiveBookingUrl = bookingUrl || apiBookingUrl || '';

  return (
    <div className="momentje-widget">
      <AvailabilityCalendar
        availabilityData={availabilityData}
        roomName={roomInfo.name}
        roomDescription={roomInfo.description}
        bookingUrl={effectiveBookingUrl}
        locale={locale}
        hotelId={hotelSlug}
        roomId={roomId}
        onDateChange={onDateSelect}
        initialArrival={initialArrival}
        initialDeparture={initialDeparture}
      />
    </div>
  );
}

/**
 * Momentje Calendar Widget
 * Embeddable availability calendar for hotel bookings
 */
class MomentjeWidget {
  private container: HTMLElement;
  private config: MomentjeWidgetConfig;
  private root: ReactDOM.Root | null = null;

  /**
   * Create a new Momentje widget instance
   * @param selector - CSS selector or HTMLElement where the widget should be rendered
   * @param config - Widget configuration
   */
  constructor(selector: string | HTMLElement, config: MomentjeWidgetConfig) {
    const element =
      typeof selector === 'string' ? document.querySelector(selector) : selector;

    if (!element) {
      throw new Error(
        `Momentje Widget: Element not found - ${typeof selector === 'string' ? selector : 'provided element'}`
      );
    }

    this.container = element as HTMLElement;
    this.config = {
      locale: 'en',
      ...config,
    };

    this.render();
  }

  /**
   * Render the widget
   */
  private render() {
    if (!this.root) {
      this.root = ReactDOM.createRoot(this.container);
    }

    this.root.render(
      <React.StrictMode>
        <WidgetWrapper {...this.config} />
      </React.StrictMode>
    );
  }

  /**
   * Update widget configuration and re-render
   * @param config - Partial configuration to update
   */
  public updateConfig(config: Partial<MomentjeWidgetConfig>) {
    this.config = { ...this.config, ...config };
    this.render();
  }

  /**
   * Destroy the widget and clean up
   */
  public destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

/**
 * Hotel Overview Widget Wrapper
 * Handles data fetching and state for hotel overview display
 */
interface HotelOverviewWrapperProps extends MomentjeHotelOverviewConfig {}

function HotelOverviewWrapper(props: HotelOverviewWrapperProps) {
  const {
    apiUrl,
    hotelSlug,
    locale = 'en',
    roomUrls,
    onRoomSelect,
    onError,
    onLoadingChange,
  } = props;

  // Handle room selection - navigate if URL configured, then call callback
  const handleRoomSelect = (packageReference: string, roomName: string) => {
    // Track room selection
    trackSelectRoom(hotelSlug, packageReference, roomName);

    if (roomUrls && roomUrls[packageReference]) {
      window.location.href = roomUrls[packageReference];
    }
    onRoomSelect?.(packageReference, roomName);
  };

  const [hotelData, setHotelData] = useState<HotelOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const loadStartTime = useRef<number>(Date.now());

  useEffect(() => {
    async function loadHotelOverview() {
      setLoading(true);
      onLoadingChange?.(true);

      try {
        const response = await fetchHotelOverview({
          apiUrl,
          hotelSlug,
          locale,
        });

        setHotelData(response);
        setError(null);

        // Track hotel view
        const loadTimeMs = Date.now() - loadStartTime.current;
        trackViewHotel(hotelSlug, response.hotel.name);
        trackWidgetLoad(hotelSlug, 'overview', loadTimeMs);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load hotel overview');
        setError(error);
        onError?.(error);

        // Track error
        trackWidgetError(error.message, hotelSlug);
      } finally {
        setLoading(false);
        onLoadingChange?.(false);
      }
    }

    loadHotelOverview();
  }, [apiUrl, hotelSlug, locale, onError, onLoadingChange]);

  if (loading) {
    return (
      <div className="momentje-widget">
        <div className="w-full max-w-6xl mx-auto border rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="text-gray-500 mb-2">Loading hotel information...</div>
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="momentje-widget">
        <div className="w-full max-w-6xl mx-auto border rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-900">Error</h3>
          </div>
          <div className="flex items-center justify-center p-8 text-center">
            <div className="max-w-md">
              <div className="text-red-600 mb-4">
                <svg
                  className="w-12 h-12 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Hotel</h3>
              <p className="text-gray-600 text-sm">{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hotelData || !hotelData.rooms || hotelData.rooms.length === 0) {
    return (
      <div className="momentje-widget">
        <div className="w-full max-w-6xl mx-auto border rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-900">No Rooms Available</h3>
          </div>
          <div className="flex items-center justify-center p-8 text-center">
            <div className="max-w-md">
              <p className="text-gray-600 text-sm">
                No rooms found for this hotel. Please try again later.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get translations for the current locale
  const translations = {
    viewAvailability: locale === 'nl' ? 'Beschikbaarheid bekijken' :
                       locale === 'de' ? 'Verfügbarkeit anzeigen' :
                       locale === 'es' ? 'Ver disponibilidad' :
                       locale === 'fr' ? 'Voir disponibilités' :
                       locale === 'it' ? 'Vedi disponibilità' :
                       locale === 'cs' ? 'Zobrazit dostupnost' :
                       locale === 'pl' ? 'Zobacz dostępność' :
                       locale === 'sk' ? 'Zobraziť dostupnosť' :
                       'View Availability',
    viewDetails: locale === 'nl' ? 'Details bekijken' :
                 locale === 'de' ? 'Details anzeigen' :
                 locale === 'es' ? 'Ver detalles' :
                 locale === 'fr' ? 'Voir les détails' :
                 locale === 'it' ? 'Vedi dettagli' :
                 locale === 'cs' ? 'Zobrazit podrobnosti' :
                 locale === 'pl' ? 'Zobacz szczegóły' :
                 locale === 'sk' ? 'Zobraziť podrobnosti' :
                 'View Details',
    roomDescription: locale === 'nl' ? 'Kamerbeschrijving' :
                      locale === 'de' ? 'Beschreibung' :
                      locale === 'es' ? 'Descripción' :
                      locale === 'fr' ? 'Description' :
                      locale === 'it' ? 'Descrizione' :
                      locale === 'cs' ? 'Popis' :
                      locale === 'pl' ? 'Opis' :
                      locale === 'sk' ? 'Popis' :
                      'Room Description',
    roomFeatures: locale === 'nl' ? 'Kamervoorzieningen' :
                  locale === 'de' ? 'Ausstattung' :
                  locale === 'es' ? 'Instalaciones' :
                  locale === 'fr' ? 'Équipements' :
                  locale === 'it' ? 'Servizi' :
                  locale === 'cs' ? 'Vybavení' :
                  locale === 'pl' ? 'Udogodnienia' :
                  locale === 'sk' ? 'Vybavenie' :
                  'Room Features',
    from: locale === 'nl' ? 'Vanaf' :
          locale === 'de' ? 'Ab' :
          locale === 'es' ? 'Desde' :
          locale === 'fr' ? 'À partir de' :
          locale === 'it' ? 'Da' :
          locale === 'cs' ? 'Od' :
          locale === 'pl' ? 'Od' :
          locale === 'sk' ? 'Od' :
          'From',
    perNight: locale === 'nl' ? '/ nacht' :
              locale === 'de' ? '/ Nacht' :
              locale === 'es' ? '/ noche' :
              locale === 'fr' ? '/ nuit' :
              locale === 'it' ? '/ notte' :
              locale === 'cs' ? '/ noc' :
              locale === 'pl' ? '/ noc' :
              locale === 'sk' ? '/ noc' :
              '/ night',
    guests: locale === 'nl' ? 'gasten' :
            locale === 'de' ? 'Gäste' :
            locale === 'es' ? 'huéspedes' :
            locale === 'fr' ? 'personnes' :
            locale === 'it' ? 'ospiti' :
            locale === 'cs' ? 'hosté' :
            locale === 'pl' ? 'goście' :
            locale === 'sk' ? 'hostia' :
            'guests',
    newBadge: locale === 'nl' ? 'NIEUW' :
              locale === 'de' ? 'NEU' :
              locale === 'es' ? 'NUEVO' :
              locale === 'fr' ? 'NOUVEAU' :
              locale === 'it' ? 'NUOVO' :
              locale === 'cs' ? 'NOVÝ' :
              locale === 'pl' ? 'NOWY' :
              locale === 'sk' ? 'NOVÝ' :
              'NEW',
  };

  return (
    <div className="momentje-widget">
      <HotelOverview
        hotel={hotelData.hotel}
        rooms={hotelData.rooms}
        onRoomSelect={handleRoomSelect}
        translations={translations}
      />
    </div>
  );
}

/**
 * Momentje Hotel Overview Widget
 * Embeddable hotel overview showing all rooms
 */
class MomentjeHotelOverviewWidget {
  private container: HTMLElement;
  private config: MomentjeHotelOverviewConfig;
  private root: ReactDOM.Root | null = null;

  /**
   * Create a new Momentje Hotel Overview widget instance
   * @param selector - CSS selector or HTMLElement where the widget should be rendered
   * @param config - Widget configuration
   */
  constructor(selector: string | HTMLElement, config: MomentjeHotelOverviewConfig) {
    const element =
      typeof selector === 'string' ? document.querySelector(selector) : selector;

    if (!element) {
      throw new Error(
        `Momentje Hotel Overview Widget: Element not found - ${typeof selector === 'string' ? selector : 'provided element'}`
      );
    }

    this.container = element as HTMLElement;
    this.config = {
      locale: 'en',
      ...config,
    };

    this.render();
  }

  /**
   * Render the widget
   */
  private render() {
    if (!this.root) {
      this.root = ReactDOM.createRoot(this.container);
    }

    this.root.render(
      <React.StrictMode>
        <HotelOverviewWrapper {...this.config} />
      </React.StrictMode>
    );
  }

  /**
   * Update widget configuration and re-render
   * @param config - Partial configuration to update
   */
  public updateConfig(config: Partial<MomentjeHotelOverviewConfig>) {
    this.config = { ...this.config, ...config };
    this.render();
  }

  /**
   * Destroy the widget and clean up
   */
  public destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

// Export types for TypeScript users
export type { MomentjeWidgetConfig, MomentjeHotelOverviewConfig, StockItem, HotelOverviewData } from './types';

// UMD global export for script tag usage
if (typeof window !== 'undefined') {
  (window as any).MomentjeWidget = MomentjeWidget;
  (window as any).MomentjeHotelOverviewWidget = MomentjeHotelOverviewWidget;
}

export default MomentjeWidget;
export { MomentjeWidget, MomentjeHotelOverviewWidget };
